/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";
import { QueryResult, useMutation } from "@apollo/client";
import { values, union } from "lodash";
import css from "classnames";
import {
  DocumentsUploadComplete,
  DocumentsUploadCompleteVariables,
  FetchInterestDocuments,
  FetchInterestDocumentsVariables,
  RequestDocumentPreflights,
  RequestDocumentPreflightsVariables,
} from "types/gql-types";
import { Button } from "components/haven-design/buttons";
import { userLoginToken } from "utils/general-utils";
import { putFile } from "utils/put-file";
import { DOCUMENT_COMPLETE, DOCUMENT_PREFLIGHT } from "../query";
import { Actions, Document, HTTPStatus, ActionTypes, State } from "../reducer";
import { ALLOWED_FILE_TYPES } from "../constants";
import styles from "./index.module.css";

const exts = union(...ALLOWED_FILE_TYPES.map((ft) => ft.ext));

// Need a global reference here due to interaction between ref callback and needing to
// trigger file upload dialogue only once by leveraging useEffect
// eslint-disable-next-line init-declarations
let uploadButtonRefNode: HTMLInputElement;

// Manually setting onchange in uploadButtonRef is necessary due to
// some sort of low-level React bug where, upon programatically
// clicking the uploadButtonRefNode, it immediately destroys and recreates the element.
// During this destruction, the onChange handler specified later in the render does not fire.
// Due to this, any file selected by the dialogue will not fire the correct onChange handler
// and won't be prepped for upload. By manually setting this here, we
// correctly trigger the onChange handler and are able to work with the file again.
// We have to ignore this due to the difference in the signature
// between HTMLInputEventHandler onchange and React's synthetic event handlers.
type NavigateFunctionLike = (to: string, options?: { replace?: boolean; state?: any }) => void;
type LocationLike = { pathname: string; state?: { addDocumentIndex?: number } };

type DocumentUploaderType = {
  docs: Document[];
  state: State;
  dispatch: React.Dispatch<ActionTypes>;
  interestID: string;
  query: QueryResult<FetchInterestDocuments, FetchInterestDocumentsVariables>;

  location?: LocationLike;
  navigate?: NavigateFunctionLike;
};

const DocumentUploader: React.FC<DocumentUploaderType> =
({ docs, state, dispatch, interestID, query, location, navigate }) => {
  // GraphQL mutation handlers for starting and completing document uploads.
  const [preflight] = useMutation<RequestDocumentPreflights, RequestDocumentPreflightsVariables>(
    DOCUMENT_PREFLIGHT,
  );

  const [complete] = useMutation<DocumentsUploadComplete, DocumentsUploadCompleteVariables>(
    DOCUMENT_COMPLETE,
  );

  // used to keep track of whether we've added a document when the "Add document" button
  // is clicked elsewhere
  const [readyForUploadClick, setReadyForUploadClick] = useState(false);

  // Filter function returns only selected documents that are valid and haven't
  // been uploaded yet.
  const uploadable = (doc: Document) => doc.validation === null && doc.httpStatus === null;

  const isAnyUploading = docs.some((doc) => doc.httpStatus === HTTPStatus.Loading);
  const isAnyUploadable = docs.filter(uploadable).length > 0;

  // onSubmitUpload manages the dance thats initiated when the selected
  // documents are submitted for upload. The following high-level requests
  // occur:
  //  1. Sends one GraphQL mutation for creating one or more document preflight
  //    requests. This mutation returns a list of the document objects, the
  //    temporary ID associated with that request, and a pre-signed S3 URL
  //    which will be used when uploading the document contents to S3.
  //  2. Once the preflight request is completed, one or more HTTP requests
  //    are started to the S3 service, each containing the contents of one of
  //    the documents.
  //  3. Once all S3 uploads are completed, the documents which were
  //    successfully uploaded are then collected and sent in one last GraphQL
  //    mutation to mark them as having been successfully uploaded.
  //  4. Finally, once the uploaded documents are marked as completed, the
  //    query to fetch all uploaded documents for the current interest is
  //    performed and the UI is updated to reflect the newly uploaded
  //    documents.
  const onSubmitUpload = () => {
    const token = userLoginToken();
    if (token === null) {
      throw new Error("Unexpected absence of the user's login token.");
    }

    // Create a set of preflight document request objects which will be passed
    // as a variable to the document preflight GraphQL mutation.
    const documents = docs.filter(uploadable).map((document) => ({
      tempId: document.id,
      filename: document.file.name,
      filesize: document.file.size,
      category: document.category as string,
      interest: interestID,
    }));

    // Update the local state of the selected documents as having begun their
    // upload journey.
    dispatch({
      type: Actions.StartPreflight,
      payload: { documents: documents.map((document) => document.tempId) },
    });

    // Perform the document preflight request. Once completed, individual HTTP
    // requests will be performed with the returned pre-signed S3 URLs.
    preflight({ variables: { documents } }).then((response) => {
      if (!response.data) {
        return;
      }

      const { documentPreflight } = response.data;

      const requests = documentPreflight.map(
        ({ uploadUrl, tempId, document }) =>
          new Promise<string>((resolve, reject) => {
            if (!(tempId in state)) {
              throw new Error("Unexpected response from the server.");
            }

            putFile(uploadUrl, state[tempId].file)
              .then((fetchResponse) => {
                dispatch({
                  type: Actions.S3UploadResponse,
                  payload: { id: tempId, response: fetchResponse },
                });

                if (fetchResponse.status >= 200 && fetchResponse.status < 300) {
                  resolve(document.id);
                  return;
                }

                reject(document.id);
              })
              .catch(() => {
                dispatch({
                  type: Actions.S3UploadResponse,
                  payload: { id: tempId, response: new Response(null, { status: 503 }) },
                });
                reject(document.id);
              });
          }),
      );

      // The document already has its HTTP status. Handle the rejection so a failed
      // PUT does not become an unhandled Promise.all rejection.
      Promise.all(requests)
        .then((fetchedDocuments) =>
          complete({ variables: { documents: fetchedDocuments } }).then(() => query.refetch()))
        .catch(() => undefined);
    });
  };
  const onFileInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    dispatch({
      type: Actions.SelectFiles,
      payload: {
        files: values(target.files),
      },
    });
  };

  const uploadButtonRef = (node: HTMLInputElement) => {
    if (!node) return null;
    // @ts-ignore
    node.onchange = onFileInputChange;
    uploadButtonRefNode = node;
    setReadyForUploadClick(true);

    return node;
  };

  const FileUploadButton = () => (
    <input
      type="file"
      id="documentUpload"
      ref={uploadButtonRef}
      accept={exts.join(", ")}
      multiple
      onChange={onFileInputChange}
    />
  );


  const addDocumentIndex = (location?.state?.addDocumentIndex) || -1;
  useEffect(
    () => {
      if (readyForUploadClick && addDocumentIndex !== -1) {
        uploadButtonRefNode.click();

        // cleanup nav state so refresh doesn't cause duplicate upload submissions
        if (navigate && location) {
          navigate(location.pathname, { state: {} });
        }
      }
    },
    // Only fire this effect when it's both ready for upload click (the ref we click is not null)
    // and addDocumentIndex has changed.
    [readyForUploadClick, addDocumentIndex],
  );
  return (
    <div className={css("form-group", styles.actions)}>
      <div className={styles.select}>
        <FileUploadButton />
      </div>

      <div className={styles.submit}>
        <Button
          size="S"
          color="primary"
          style={{ marginRight: 8 }}
          onClick={onSubmitUpload}
          disabled={!isAnyUploadable}
        >
      Start uploading&nbsp;
          {docs.length > 0 && (
            <span className={css(styles.badge, "badge")}>
              {docs.filter(uploadable).length}
            </span>
          )}
        </Button>
        <Button
          size="S"
          color="secondary"
          disabled={docs.length === 0 || isAnyUploading}
          onClick={() => {
            dispatch({ type: Actions.ResetSelection, payload: {} });
          }}
        >
      Reset selected files
        </Button>
      </div>
    </div>);
};

export default DocumentUploader;
